import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { api } from '../services/apiClient';
import ProductCard from '../components/products/ProductCard';
import BackButton from '../components/ui/BackButton';

const CategoryPage = () => {
  const { categorySlug, categoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialMount = useRef(true);
  const scrollTargetLevel = useRef(null);
  const shouldScrollToProducts = useRef(false);
  
  // Track if we need to resolve a route-based category (e.g. /products/:slug) before fetching products.
  // This prevents a race where fetchProducts fires with [] before the hierarchy is resolved.
  const pendingCategoryResolution = useRef(false);
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get('page'), 10) || 1,
    limit: 12,
    total: 0,
    totalPages: 0,
  });
  
  // Filter states - using an array to support unlimited nesting levels
  // Initialize from URL so state is correct from the very first render (critical for back navigation)
  const [categoryHierarchy, setCategoryHierarchy] = useState(() => {
    const urlHierarchy = searchParams.get('categoryHierarchy');
    if (urlHierarchy) {
      try {
        const hierarchy = JSON.parse(urlHierarchy);
        if (Array.isArray(hierarchy) && hierarchy.length > 0) {
          return hierarchy;
        }
      } catch (e) { /* ignore parse errors */ }
    }
    return [];
  });
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || ''); // Local input state
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || ''); // Applied search term
  const [minPrice, setMinPrice] = useState(searchParams.get('minPrice') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('maxPrice') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');
  
  // On first render only: if route has a category slug/id but hierarchy is empty,
  // we need to wait for categories to load before we can resolve and fetch.
  if (isInitialMount.current && categoryHierarchy.length === 0 && (categorySlug || categoryId)) {
    pendingCategoryResolution.current = true;
  }
  
  // Dropdown states - dynamic based on hierarchy depth
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [isPriceDropdownOpen, setIsPriceDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);

  // Sort products function
  const sortProducts = (productsToSort, sortOption) => {
    const sorted = [...productsToSort];
    
    switch (sortOption.toLowerCase()) {
      case 'newest':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB - dateA; // Newest first
        });
      
      case 'price: low to high':
        return sorted.sort((a, b) => {
          const priceA = Number(a.price) || 0;
          const priceB = Number(b.price) || 0;
          return priceA - priceB;
        });
      
      case 'price: high to low':
        return sorted.sort((a, b) => {
          const priceA = Number(a.price) || 0;
          const priceB = Number(b.price) || 0;
          return priceB - priceA;
        });
      
      case 'name: a-z':
        return sorted.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      
      default:
        return sorted;
    }
  };

  // Root-to-leaf id chain for a category, so deep links such as
  // /category/<subcategory-id> select every level of the filters and breadcrumb.
  const getCategoryChain = (categoryId) => {
    const byId = new Map(categories.map(cat => [cat.id, cat]));
    const chain = [];
    let current = byId.get(categoryId);
    while (current && !chain.includes(current.id)) {
      chain.unshift(current.id);
      current = byId.get(current.parent_category_id);
    }
    return chain;
  };

  // Initial load: fetch categories and resolve route-based category params
  useEffect(() => {
    const initializePage = async () => {
      await fetchCategories();
      isInitialMount.current = false;
    };
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle route-param-based category (e.g. /products/:categorySlug or /category/:categoryId)
  // Only needed when there's no saved hierarchy in the URL (i.e. first visit via a category link)
  // This effect MUST clear pendingCategoryResolution so fetchProducts knows it's safe to proceed.
  useEffect(() => {
    if (categories.length === 0) return;
    
    // If hierarchy is already set (from URL query params or a previous resolution), mark resolved
    if (categoryHierarchy.length > 0) {
      pendingCategoryResolution.current = false;
      return;
    }
    
    // Resolve categorySlug or categoryId from the route path
    if (categorySlug || categoryId) {
      const category = categories.find(cat => 
        cat.slug === categorySlug || cat.id === categoryId
      );
      if (category) {
        setCategoryHierarchy(getCategoryChain(category.id));
        // Don't clear pendingCategoryResolution yet - the state update hasn't applied.
        // It will be cleared on the next render when categoryHierarchy.length > 0.
        return;
      }
    }
    
    // No resolution needed (or category not found) - safe to fetch
    pendingCategoryResolution.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.length, categoryHierarchy.length]);

  // Update selected category when route params change (e.g. navigating between /products/shoes and /products/hats)
  useEffect(() => {
    // Skip on initial mount - handled by initialization effects above
    if (isInitialMount.current || categories.length === 0) return;
    
    if (categorySlug || categoryId) {
      const category = categories.find(cat => 
        cat.slug === categorySlug || cat.id === categoryId
      );
      const chain = category ? getCategoryChain(category.id) : [];
      if (category && chain.join('/') !== categoryHierarchy.join('/')) {
        setCategoryHierarchy(chain);
      }
    } else if (!categorySlug && !categoryId) {
      // If navigating to /products with no slug/id, only clear if there's no query param hierarchy
      const urlHierarchy = searchParams.get('categoryHierarchy');
      if (!urlHierarchy && categoryHierarchy.length > 0) {
        setCategoryHierarchy([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySlug, categoryId]);

  // Sync search term with URL params (for navbar search)
  useEffect(() => {
    if (isInitialMount.current) return;
    
    const urlSearchTerm = searchParams.get('search') || '';
    if (urlSearchTerm !== searchTerm) {
      setSearchInput(urlSearchTerm);
      setSearchTerm(urlSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch products when filters change or categories are loaded
  useEffect(() => {
    if (categories.length === 0) return;
    // Wait for route-based category to resolve before fetching (prevents double-fetch)
    if (pendingCategoryResolution.current) return;
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryHierarchy, searchTerm, minPrice, maxPrice, pagination.page, categories.length]);

  // Update query params when filters change
  useEffect(() => {
    if (categories.length > 0 && !isInitialMount.current) {
      updateQueryParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, minPrice, maxPrice, categoryHierarchy, sortBy, pagination.page]);

  // Sort products when sortBy changes
  useEffect(() => {
    if (products.length > 0) {
      const sortedProducts = sortProducts([...products], sortBy);
      setProducts(sortedProducts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  const fetchCategories = async () => {
    try {
      // Session storage keys (shared with HomePage and TopNav)
      const CATEGORIES_KEY = 'homepage_categories';
      const CATEGORY_TREE_KEY = 'category_tree';
      
      let flatData = null;
      let treeData = null;
      
      // Try to get data from session storage first
      const cachedCategories = sessionStorage.getItem(CATEGORIES_KEY);
      const cachedTree = sessionStorage.getItem(CATEGORY_TREE_KEY);
      
      if (cachedCategories) {
        flatData = JSON.parse(cachedCategories);
      }
      if (cachedTree) {
        treeData = JSON.parse(cachedTree);
      }
      
      // Fetch from API if not in cache
      if (!flatData || !treeData) {
        const promises = [];
        
        if (!flatData) {
          promises.push(api.categories.list({ is_active: true }));
        } else {
          promises.push(Promise.resolve(flatData));
        }
        
        if (!treeData) {
          promises.push(api.categories.getTree().catch(() => []));
        } else {
          promises.push(Promise.resolve(treeData));
        }
        
        const [flatResult, treeResult] = await Promise.all(promises);
        
        // Update variables and cache
        if (!flatData) {
          flatData = flatResult;
          sessionStorage.setItem(CATEGORIES_KEY, JSON.stringify(flatResult));
        }
        
        if (!treeData) {
          treeData = treeResult;
          sessionStorage.setItem(CATEGORY_TREE_KEY, JSON.stringify(treeResult));
        }
      }
      
      // Set categories first
      setCategories(flatData);
      setCategoryTree(treeData);
      
      // Don't set category hierarchy here - let the initialization code handle it
      // to avoid race conditions with URL parameter restoration
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Get subcategories of any category by ID
  const getSubcategories = (parentId) => {
    if (!parentId) return [];
    return categories.filter(cat => cat.parent_category_id === parentId);
  };

  // Get category hierarchy levels - returns array of subcategory arrays for each level
  const getCategoryLevels = () => {
    const levels = [];
    
    // Root level categories
    const rootCategories = categories.filter(cat => !cat.parent_category_id);
    levels.push(rootCategories);
    
    // For each selected category in hierarchy, get its children
    categoryHierarchy.forEach((categoryId, index) => {
      const children = getSubcategories(categoryId);
      if (children.length > 0) {
        levels.push(children);
      }
    });
    
    return levels;
  };

  const categoryLevels = getCategoryLevels();
  
  // Get the currently selected leaf category (deepest in hierarchy)
  const currentLeafCategory = categoryHierarchy.length > 0 
    ? categories.find(cat => cat.id === categoryHierarchy[categoryHierarchy.length - 1])
    : null;
    
  // Build full category path for breadcrumb
  const getCategoryPath = () => {
    return categoryHierarchy.map(id => categories.find(cat => cat.id === id)).filter(Boolean);
  };
  
  const categoryPath = getCategoryPath();

  // Get root categories (parent categories)
  const rootCategories = categories.filter(cat => !cat.parent_category_id && cat.is_active);

  // Get selected parent category and its children
  const selectedParentCategory = categoryHierarchy.length > 0 
    ? categories.find(cat => cat.id === categoryHierarchy[0])
    : null;
  const parentChildren = selectedParentCategory 
    ? getSubcategories(selectedParentCategory.id).filter(cat => cat.is_active)
    : [];

  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      
      // Use the deepest category in the hierarchy (most specific)
      if (categoryHierarchy.length > 0) {
        params.category_id = categoryHierarchy[categoryHierarchy.length - 1];
      }
      
      if (searchTerm) params.search = searchTerm;
      if (minPrice) params.min_price = parseFloat(minPrice);
      if (maxPrice) params.max_price = parseFloat(maxPrice);
      
      const data = await api.products.getCatalog(params);
      
      // Apply sorting to fetched products
      const sortedProducts = sortProducts(data.items || [], sortBy);
      setProducts(sortedProducts);
      setPagination({
        page: data.page,
        limit: data.limit,
        total: data.total,
        totalPages: data.total_pages,
      });
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
      
      // Scroll to products after loading is complete
      if (shouldScrollToProducts.current) {
        setTimeout(() => {
          const productsElement = document.getElementById('products-grid');
          if (productsElement) {
            productsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          shouldScrollToProducts.current = false;
        }, 100);
      }
    }
  };

  const updateQueryParams = () => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (minPrice) params.minPrice = minPrice;
    if (maxPrice) params.maxPrice = maxPrice;
    if (sortBy && sortBy !== 'newest') params.sort = sortBy;
    if (pagination.page > 1) params.page = String(pagination.page);
    
    // Store the full category hierarchy in URL
    if (categoryHierarchy.length > 0) {
      params.categoryHierarchy = JSON.stringify(categoryHierarchy);
      // Also keep the category slug for readability
      const leafCategory = categories.find(cat => cat.id === categoryHierarchy[categoryHierarchy.length - 1]);
      if (leafCategory) params.category = leafCategory.slug;
    }
    setSearchParams(params);
  };

  // Handle category selection at any level
  const handleCategorySelect = (categoryId, level, shouldScroll = false) => {
    if (!categoryId) {
      // Clear selections at this level and beyond
      if (level === 0) {
        // If at root level, clear everything
        setCategoryHierarchy([]);
      } else {
        // Otherwise, keep hierarchy up to the previous level
        setCategoryHierarchy(categoryHierarchy.slice(0, level));
      }
    } else {
      // Update hierarchy up to this level
      const newHierarchy = [...categoryHierarchy.slice(0, level), categoryId];
      setCategoryHierarchy(newHierarchy);
      
      if (shouldScroll) {
        scrollTargetLevel.current = level;
      }
    }
    
    // Clear search filters when selecting a category
    setSearchInput('');
    setSearchTerm('');
    setMinPrice('');
    setMaxPrice('');
    
    setPagination({ ...pagination, page: 1 });
    setOpenDropdownIndex(null);
  };

  useEffect(() => {
    if (scrollTargetLevel.current !== null) {
      setTimeout(() => {
        const element = document.getElementById(`subcategory-level-${scrollTargetLevel.current}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // If no subcategory level exists, scroll to products
          const productsElement = document.getElementById('products-grid');
          if (productsElement) {
            productsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
        scrollTargetLevel.current = null;
      }, 100);
    }
  }, [categoryHierarchy]);

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearchSubmit = () => {
    setSearchTerm(searchInput.trim());
    setPagination({ ...pagination, page: 1 });
  };

  const handleClearFilters = () => {
    setCategoryHierarchy([]);
    setSearchInput('');
    setSearchTerm('');
    setMinPrice('');
    setMaxPrice('');
    setPagination({ ...pagination, page: 1 });
    setSearchParams({});
  };

  const handleSortChange = (sort) => {
    setSortBy(sort);
    setIsSortDropdownOpen(false);
  };

  // Count active filters
  const activeFiltersCount = 
    categoryHierarchy.length +
    (searchTerm ? 1 : 0) + 
    (minPrice || maxPrice ? 1 : 0);

  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <style>{`
        @media (max-width: 768px) {
          .filter-dropdown,
          .sort-dropdown {
            flex: 1 1 100% !important;
            min-width: 100% !important;
            max-width: 100% !important;
            width: 100% !important;
          }
          
          .filter-search-input {
            width: 100% !important;
          }
          
          .filter-search-input input {
            flex: 1 !important;
            min-width: 0 !important;
          }
          
          .filter-dropdown-menu,
          .sort-dropdown-menu {
            position: absolute !important;
            left: 0 !important;
            right: auto !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 100% !important;
            top: calc(100% + 4px) !important;
            max-height: min(60vh, 400px) !important;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
          }
          
          .sort-dropdown-menu {
            right: 0 !important;
            left: auto !important;
          }

          .filters-right-section {
            width: 100% !important;
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
            margin-top: 8px !important;
          }

          .filters-right-section .sort-dropdown {
            width: 100% !important;
          }

          .filters-right-section > span {
            text-align: center !important;
            width: 100% !important;
            font-size: 13px !important;
          }

          /* Ensure dropdowns don't overflow viewport */
          .filter-dropdown,
          .sort-dropdown {
            overflow: visible !important;
          }
        }

        @media (max-width: 480px) {
          .category-page-container {
            padding: 0 16px !important;
          }

          .filter-dropdown-menu,
          .sort-dropdown-menu {
            max-height: min(50vh, 350px) !important;
          }
        }

        @media (max-width: 768px) {
          .category-page-container {
            padding: 0 16px !important;
          }

          .filters-bar {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }

          .filters-bar > div:first-child {
            width: 100% !important;
          }
        }
      `}</style>
      {/* Main Container */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 24px',
      }} className="category-page-container">
        {/* Back Button */}
        <div style={{ paddingTop: '24px', paddingBottom: '16px' }}>
          <BackButton 
            to="/"
            label="Back to Home"
            variant="ghost"
            size="sm"
          />
        </div>

        {/* Page Header */}
        <div style={{
          marginBottom: '32px',
        }}>
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 400,
            color: '#000',
            marginBottom: '8px',
            letterSpacing: '-0.02em',
          }}>
            {currentLeafCategory ? currentLeafCategory.name : 'All Products'}
          </h1>
          
          {/* Breadcrumb */}
          <nav style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            color: '#666',
            flexWrap: 'wrap',
          }}>
            <Link to="/" style={{ color: '#666', textDecoration: 'none' }}>
              Home
            </Link>
            <span>›</span>
            {categoryPath.length === 0 ? (
              <span style={{ color: '#000' }}>All Products</span>
            ) : (
              categoryPath.map((cat, index) => (
                <React.Fragment key={cat.id}>
                  <span style={{ 
                    color: index === categoryPath.length - 1 ? '#000' : '#666',
                    cursor: index < categoryPath.length - 1 ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (index < categoryPath.length - 1) {
                      handleCategorySelect(cat.id, index);
                    }
                  }}
                  >
                    {cat.name}
                  </span>
                  {index < categoryPath.length - 1 && <span>›</span>}
                </React.Fragment>
              ))
            )}
          </nav>
        </div>

        {/* Filters Bar */}
        <div className="filters-bar" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px',
          paddingBottom: '24px',
          borderBottom: '1px solid #E5E5E5',
          flexWrap: 'wrap',
          gap: '16px',
        }}>
          {/* Left: Filter Dropdowns */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            flex: '1 1 100%',
            minWidth: 0,
          }}>
            {/* Dynamic Category Level Dropdowns */}
            {categoryLevels.map((levelCategories, levelIndex) => {
              const selectedAtThisLevel = categoryHierarchy[levelIndex];
              const selectedCategory = levelCategories.find(cat => cat.id === selectedAtThisLevel);
              const isOpen = openDropdownIndex === levelIndex;
              
              // Determine the label for this level
              let label = 'Category';
              if (levelIndex === 0) {
                label = 'Category';
              } else if (levelIndex === 1) {
                label = 'Subcategory';
              } else {
                // For level 3+, show "All {ParentCategoryName}"
                const parentCategory = categoryPath[levelIndex - 1];
                label = parentCategory ? `All ${parentCategory.name}` : `Level ${levelIndex + 1}`;
              }
              
              return (
                <div key={levelIndex} style={{ position: 'relative', flex: '1 1 140px', minWidth: '140px', maxWidth: '100%' }} className="filter-dropdown">
                  <button
                    onClick={() => setOpenDropdownIndex(isOpen ? null : levelIndex)}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: '#FFF',
                      border: '1px solid #D1D1D1',
                      borderRadius: '4px',
                      fontSize: '14px',
                      color: '#000',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontFamily: "'Lato', sans-serif",
                      width: '100%',
                      justifyContent: 'space-between',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#000';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#D1D1D1';
                    }}
                  >
                    <span style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {selectedCategory ? selectedCategory.name : label}
                    </span>
                    <span style={{ fontSize: '10px', flexShrink: 0 }}>▼</span>
                  </button>
                  
                  {isOpen && (
                    <>
                      <div 
                        style={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 999,
                        }}
                        onClick={() => setOpenDropdownIndex(null)}
                      />
                      <div 
                        className="filter-dropdown-menu"
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          left: 0,
                          backgroundColor: '#FFF',
                          border: '1px solid #D1D1D1',
                          borderRadius: '4px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          minWidth: '200px',
                          maxWidth: 'calc(100vw - 48px)',
                          maxHeight: '300px',
                          overflowY: 'auto',
                          zIndex: 1000,
                        }}
                      >
                        {/* "All" option for this level */}
                        {levelIndex > 0 && (
                          <div
                            onClick={() => handleCategorySelect(null, levelIndex)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              backgroundColor: !selectedAtThisLevel ? '#F5F5F5' : '#FFF',
                              fontWeight: !selectedAtThisLevel ? 600 : 400,
                            }}
                            onMouseEnter={(e) => {
                              if (selectedAtThisLevel) e.currentTarget.style.backgroundColor = '#F9F9F9';
                            }}
                            onMouseLeave={(e) => {
                              if (selectedAtThisLevel) e.currentTarget.style.backgroundColor = '#FFF';
                            }}
                          >
                            All {categoryPath[levelIndex - 1]?.name || 'Products'}
                          </div>
                        )}
                        
                        {/* Root level "All Products" */}
                        {levelIndex === 0 && (
                          <div
                            onClick={() => handleCategorySelect(null, 0)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              backgroundColor: categoryHierarchy.length === 0 ? '#F5F5F5' : '#FFF',
                              fontWeight: categoryHierarchy.length === 0 ? 600 : 400,
                            }}
                            onMouseEnter={(e) => {
                              if (categoryHierarchy.length > 0) e.currentTarget.style.backgroundColor = '#F9F9F9';
                            }}
                            onMouseLeave={(e) => {
                              if (categoryHierarchy.length > 0) e.currentTarget.style.backgroundColor = '#FFF';
                            }}
                          >
                            All Products
                          </div>
                        )}
                        
                        {/* Categories at this level */}
                        {levelCategories.map((cat) => (
                          <div
                            key={cat.id}
                            onClick={() => handleCategorySelect(cat.id, levelIndex)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              backgroundColor: selectedAtThisLevel === cat.id ? '#F5F5F5' : '#FFF',
                              fontWeight: selectedAtThisLevel === cat.id ? 600 : 400,
                            }}
                            onMouseEnter={(e) => {
                              if (selectedAtThisLevel !== cat.id) e.currentTarget.style.backgroundColor = '#F9F9F9';
                            }}
                            onMouseLeave={(e) => {
                              if (selectedAtThisLevel !== cat.id) e.currentTarget.style.backgroundColor = '#FFF';
                            }}
                          >
                            {cat.name}
                            {getSubcategories(cat.id).length > 0 && (
                              <span style={{ marginLeft: '8px', fontSize: '12px', color: '#999' }}>
                                ({getSubcategories(cat.id).length})
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}

            {/* Search Input with Button */}
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              flex: '1 1 200px', 
              minWidth: '200px',
            }}>
              <input
                type="text"
                placeholder="Search products..."
                value={searchInput}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchSubmit();
                  }
                }}
                className="filter-search-input"
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#FFF',
                  border: '1px solid #D1D1D1',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#000',
                  fontFamily: "'Lato', sans-serif",
                  flex: 1,
                  outline: 'none',
                  transition: 'all 0.2s ease',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#000';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#D1D1D1';
                }}
              />
              <button
                onClick={handleSearchSubmit}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#000',
                  color: '#FFF',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontFamily: "'Lato', sans-serif",
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#333';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#000';
                }}
              >
                Search
              </button>
            </div>

            {/* Clear Filters Button */}
            {activeFiltersCount > 0 && (
              <button
                onClick={handleClearFilters}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#000',
                  color: '#FFF',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontFamily: "'Lato', sans-serif",
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#333';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#000';
                }}
              >
                Clear Filters ({activeFiltersCount})
              </button>
            )}
          </div>

          {/* Right: Sort & Item Count */}
          <div className="filters-right-section" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flex: '0 0 auto',
          }}>
            {/* Sort Dropdown */}
            <div style={{ position: 'relative', flex: '1 1 180px', minWidth: '180px', maxWidth: '100%' }} className="sort-dropdown">
              <button
                onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#FFF',
                  border: '1px solid #D1D1D1',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#000',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: "'Lato', sans-serif",
                  width: '100%',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#D1D1D1';
                }}
              >
                <span style={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}
                </span>
                <span style={{ fontSize: '10px', flexShrink: 0 }}>▼</span>
              </button>
              
              {isSortDropdownOpen && (
                <>
                  <div 
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 999,
                    }}
                    onClick={() => setIsSortDropdownOpen(false)}
                  />
                  <div 
                    className="sort-dropdown-menu"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      right: 0,
                      backgroundColor: '#FFF',
                      border: '1px solid #D1D1D1',
                      borderRadius: '4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      minWidth: '180px',
                      maxWidth: 'calc(100vw - 48px)',
                      zIndex: 1000,
                    }}
                  >
                    {['Newest', 'Price: Low to High', 'Price: High to Low', 'Name: A-Z'].map((option) => {
                      const isSelected = sortBy.toLowerCase() === option.toLowerCase();
                      return (
                        <div
                          key={option}
                          onClick={() => handleSortChange(option.toLowerCase())}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            backgroundColor: isSelected ? '#F5F5F5' : '#FFF',
                            fontWeight: isSelected ? 600 : 400,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = '#F9F9F9';
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = '#FFF';
                          }}
                        >
                          <span>{option}</span>
                          {isSelected && (
                            <span style={{ color: '#000', fontSize: '16px' }}>✓</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            
            <span style={{
              fontSize: '14px',
              color: '#666',
            }}>
              {pagination.total} items
            </span>
          </div>
        </div>

        {/* Parent Categories - Small Image Cards */}
        {rootCategories.length > 0 && (
          <div style={{
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: '1px solid #E5E5E5',
          }}>
            {/* Title */}
            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              fontWeight: 400,
              color: '#000',
              marginBottom: '20px',
              letterSpacing: '-0.01em',
            }}>
              Shop by Category
            </h2>
            <div style={{
              display: 'flex',
              gap: '12px',
              overflowX: 'auto',
              paddingBottom: '8px',
              scrollbarWidth: 'thin',
              scrollbarColor: '#D1D1D1 transparent',
            }}>
              {rootCategories.map((category) => {
                const isSelected = categoryHierarchy.length > 0 && categoryHierarchy[0] === category.id;
                return (
                  <div
                    key={category.id}
                    onClick={() => handleCategorySelect(category.id, 0, true)}
                    className="category-card-container"
                    style={{
                      flexShrink: 0,
                      width: '80px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: isSelected ? 1 : 0.85,
                      transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '1';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = isSelected ? '1' : '0.85';
                      e.currentTarget.style.transform = isSelected ? 'scale(1.05)' : 'scale(1)';
                    }}
                  >
                    {/* Category Image */}
                    <div className="category-card-image" style={{
                      width: '80px',
                      height: '60px',
                      marginBottom: '6px',
                      padding: isSelected ? '2px' : '1px',
                      boxSizing: 'border-box',
                    }}>
                      <div style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '6px',
                        backgroundColor: '#F5F5F5',
                        border: isSelected ? '2px solid #000' : '1px solid #E5E5E5',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}>
                        {category.image_url ? (
                          <img
                            src={category.image_url}
                            alt={category.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          <span style={{
                            fontSize: '24px',
                            color: '#999',
                          }}>
                            📦
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Category Name */}
                    <div className="category-card-text" style={{
                      fontSize: '11px',
                      color: isSelected ? '#000' : '#666',
                      fontWeight: isSelected ? 600 : 400,
                      textAlign: 'center',
                      lineHeight: '1.2',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>
                      {category.name}
                    </div>
                  </div>
                );
              })}
            </div>
            <style>{`
              div::-webkit-scrollbar {
                height: 6px;
              }
              div::-webkit-scrollbar-track {
                background: transparent;
              }
              div::-webkit-scrollbar-thumb {
                background-color: #D1D1D1;
                border-radius: 3px;
              }
              div::-webkit-scrollbar-thumb:hover {
                background-color: #999;
              }
            `}</style>
          </div>
        )}

        {/* Dynamic Subcategory Levels - Small Image Cards */}
        {categoryHierarchy.map((selectedCategoryId, levelIndex) => {
          const children = getSubcategories(selectedCategoryId).filter(cat => cat.is_active);
          if (children.length === 0) return null;
          
          // Get the parent category name for the title
          const parentCategory = categories.find(cat => cat.id === selectedCategoryId);
          const parentCategoryName = parentCategory ? parentCategory.name : 'Category';
          
          return (
            <div 
              key={`level-${levelIndex}`}
              id={`subcategory-level-${levelIndex}`}
              style={{
                marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: '1px solid #E5E5E5',
                paddingLeft: `${(levelIndex + 1) * 20}px`,
                scrollMarginTop: '120px',
              }}
            >
              {/* Title */}
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                fontWeight: 400,
                color: '#000',
                marginBottom: '20px',
                letterSpacing: '-0.01em',
              }}>
                Browse {parentCategoryName}
              </h2>
              <div style={{
                display: 'flex',
                gap: '12px',
                overflowX: 'auto',
                paddingBottom: '8px',
                scrollbarWidth: 'thin',
                scrollbarColor: '#D1D1D1 transparent',
              }}>
                {children.map((category) => {
                  const isSelected = categoryHierarchy[levelIndex + 1] === category.id;
                  return (
                    <div
                      key={category.id}
                      onClick={() => handleCategorySelect(category.id, levelIndex + 1, true)}
                      className="category-card-container"
                      style={{
                        flexShrink: 0,
                        width: '80px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: isSelected ? 1 : 0.85,
                        transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = isSelected ? '1' : '0.85';
                        e.currentTarget.style.transform = isSelected ? 'scale(1.05)' : 'scale(1)';
                      }}
                    >
                      {/* Category Image */}
                      <div className="category-card-image" style={{
                        width: '80px',
                        height: '60px',
                        marginBottom: '6px',
                        padding: isSelected ? '2px' : '1px',
                        boxSizing: 'border-box',
                      }}>
                        <div style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '6px',
                          backgroundColor: '#F5F5F5',
                          border: isSelected ? '2px solid #000' : '1px solid #E5E5E5',
                          boxSizing: 'border-box',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                        }}>
                          {category.image_url ? (
                            <img
                              src={category.image_url}
                              alt={category.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <span style={{
                              fontSize: '24px',
                              color: '#999',
                            }}>
                              📦
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Category Name */}
                      <div className="category-card-text" style={{
                        fontSize: '11px',
                        color: isSelected ? '#000' : '#666',
                        fontWeight: isSelected ? 600 : 400,
                        textAlign: 'center',
                        lineHeight: '1.2',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {category.name}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Products Grid */}
        <div id="products-grid" style={{ scrollMarginTop: '300px' }}>
        {loading ? (
          <div className="products-grid-clean" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '32px',
            paddingBottom: '32px',
            marginBottom: '68px',
          }}>
            {/* Skeleton Loading Cards */}
            {[...Array(8)].map((_, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: '#FFF',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  animation: 'fadeIn 0.3s ease-in-out',
                  animationDelay: `${index * 0.05}s`,
                  opacity: 0,
                  animationFillMode: 'forwards',
                }}
              >
                {/* Image Skeleton */}
                <div style={{
                  width: '100%',
                  paddingTop: '100%',
                  backgroundColor: '#F5F5F5',
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                  <div className="skeleton-shimmer" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }} />
                </div>
                
                {/* Content Skeleton */}
                <div style={{ padding: '16px' }}>
                  {/* Title Skeleton */}
                  <div style={{
                    height: '20px',
                    backgroundColor: '#F5F5F5',
                    borderRadius: '4px',
                    marginBottom: '12px',
                    position: 'relative',
                    overflow: 'hidden',
                  }}>
                    <div className="skeleton-shimmer" />
                  </div>
                  
                  {/* Subtitle Skeleton */}
                  <div style={{
                    height: '16px',
                    backgroundColor: '#F5F5F5',
                    borderRadius: '4px',
                    marginBottom: '16px',
                    width: '60%',
                    position: 'relative',
                    overflow: 'hidden',
                  }}>
                    <div className="skeleton-shimmer" />
                  </div>
                  
                  {/* Price Skeleton */}
                  <div style={{
                    height: '24px',
                    backgroundColor: '#F5F5F5',
                    borderRadius: '4px',
                    width: '40%',
                    position: 'relative',
                    overflow: 'hidden',
                  }}>
                    <div className="skeleton-shimmer" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '80px 20px',
          }}>
            <p style={{ fontSize: '4rem', marginBottom: '16px' }}>📦</p>
            <h3 style={{
              fontSize: '24px',
              marginBottom: '8px',
              fontFamily: "'Playfair Display', serif",
              fontWeight: 400,
              color: '#000',
            }}>
              No products found
            </h3>
            <p style={{ fontSize: '14px', color: '#666' }}>
              Try adjusting your filters or search terms
            </p>
          </div>
        ) : (
          <>
            <div className="products-grid-clean" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '32px',
              paddingBottom: '32px',
              marginBottom: '68px',
            }}>
              {products.map((product) => (
                <ProductCard key={product.id} product={product} variant="clean" />
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '64px',
              }}>
                <button
                  onClick={() => {
                    shouldScrollToProducts.current = true;
                    setPagination({ ...pagination, page: pagination.page - 1 });
                  }}
                  disabled={pagination.page === 1}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: pagination.page === 1 ? '#F5F5F5' : '#000',
                    color: pagination.page === 1 ? '#999' : '#FFF',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (pagination.page !== 1) e.currentTarget.style.backgroundColor = '#333';
                  }}
                  onMouseLeave={(e) => {
                    if (pagination.page !== 1) e.currentTarget.style.backgroundColor = '#000';
                  }}
                >
                  Previous
                </button>
                
                <span style={{
                  fontSize: '14px',
                  color: '#000',
                  fontWeight: 500,
                  padding: '0 12px',
                }}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                
                <button
                  onClick={() => {
                    shouldScrollToProducts.current = true;
                    setPagination({ ...pagination, page: pagination.page + 1 });
                  }}
                  disabled={pagination.page === pagination.totalPages}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: pagination.page === pagination.totalPages ? '#F5F5F5' : '#000',
                    color: pagination.page === pagination.totalPages ? '#999' : '#FFF',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (pagination.page !== pagination.totalPages) e.currentTarget.style.backgroundColor = '#333';
                  }}
                  onMouseLeave={(e) => {
                    if (pagination.page !== pagination.totalPages) e.currentTarget.style.backgroundColor = '#000';
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
        </div>
      </div>

      {/* Responsive Styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .skeleton-shimmer::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.6) 50%,
            transparent 100%
          );
          animation: shimmer 1.5s infinite;
        }

        /* Mobile Responsive */}
        @media (max-width: 768px) {
          .products-grid-clean {
            grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)) !important;
            gap: 20px !important;
          }
        }

        /* Tablet */}
        @media (min-width: 769px) and (max-width: 1024px) {
          .products-grid-clean {
            grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)) !important;
            gap: 24px !important;
          }
        }

        /* Large Desktop */}
        @media (min-width: 1400px) {
          .products-grid-clean {
            grid-template-columns: repeat(4, 1fr) !important;
          }
        }

        /* Desktop Category Images */
        @media (min-width: 769px) {
          .category-card-container {
            width: 120px !important;
          }
          .category-card-image {
            width: 120px !important;
            height: 90px !important;
          }
          .category-card-text {
            font-size: 13px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CategoryPage;
